import type { PointerEvent, ReactNode } from 'react'
import { motion } from 'motion/react'
import { ArrowUpRight } from 'lucide-react'
import { useMeta } from '@/api/queries'
import type { StarterQuestion } from '@/api/types'
import { riseIn, spring, stagger } from '@/lib/motion'
import { AiOrb } from '@/components/ui/AiOrb'

const GENERIC_STARTERS: StarterQuestion[] = [
  { department: null, question: 'What is our policy on working from home?' },
  { department: null, question: 'How do I submit an expense claim?' },
  { department: null, question: 'Who do I contact for IT support?' },
]

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

/** Track the pointer so the card's spotlight follows it. */
function trackSpotlight(event: PointerEvent<HTMLElement>) {
  const rect = event.currentTarget.getBoundingClientRect()
  event.currentTarget.style.setProperty('--mx', `${event.clientX - rect.left}px`)
  event.currentTarget.style.setProperty('--my', `${event.clientY - rect.top}px`)
}

/** Welcome screen: greeting, the composer (passed in so it can glide away) and starter questions. */
export function EmptyState({ onPick, composer }: { onPick: (question: string) => void; composer: ReactNode }) {
  const meta = useMeta()
  const firstName = meta.user?.name?.split(' ')[0]
  const starters = meta.starter_questions?.length ? meta.starter_questions.slice(0, 4) : GENERIC_STARTERS

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center px-4 pb-16 pt-20 sm:px-6 sm:pt-10">
      <div className="aurora" aria-hidden />
      <motion.div
        variants={stagger(0.08, 0.05)}
        initial="hidden"
        animate="show"
        className="relative flex w-full max-w-reading flex-col items-center"
      >
        <motion.div variants={riseIn} className="relative">
          <AiOrb className="size-14" />
        </motion.div>
        <motion.h1
          variants={riseIn}
          className="mt-8 text-balance text-center text-[2.4rem] font-semibold leading-[1.08] tracking-display text-ink sm:text-[3.1rem]"
        >
          {greeting()}
          {firstName ? (
            <>
              , <span className="ai-gradient-text">{firstName}</span>
            </>
          ) : null}
          .
        </motion.h1>
        <motion.p
          variants={riseIn}
          className="mt-4 max-w-xl text-balance text-center text-[17px] leading-7 text-ink-muted"
        >
          Ask anything about {meta.org_name}&rsquo;s policies, processes and documents. Every answer shows its sources.
        </motion.p>

        <motion.div variants={riseIn} className="mt-10 w-full">
          {composer}
        </motion.div>

        <h2 className="sr-only">Suggested questions</h2>
        <motion.ul variants={stagger(0.06, 0.1)} className="mt-4 grid w-full gap-2.5 sm:grid-cols-2">
          {starters.map((starter) => (
            <motion.li key={starter.question} variants={riseIn}>
              <motion.button
                type="button"
                onClick={() => onPick(starter.question)}
                onPointerMove={trackSpotlight}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={spring.snappy}
                className="spotlight group flex h-full w-full items-start gap-3 rounded-2xl bg-surface/80 p-4 text-left shadow-card backdrop-blur transition-shadow duration-300 hover:shadow-lift"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                    {starter.department ?? 'General'}
                  </span>
                  <span className="mt-1.5 block text-[14.5px] font-medium leading-[1.45] text-ink">
                    {starter.question}
                  </span>
                </span>
                <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-subtle text-ink-faint transition-all duration-300 group-hover:rotate-45 group-hover:bg-accent group-hover:text-accent-ink">
                  <ArrowUpRight className="size-4" aria-hidden />
                </span>
              </motion.button>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </div>
  )
}
